<template>
    <div class="ah-page accounting-statement-page">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>綜合損益表</h1>
                    </div>
                    <div class="actions">
                        <el-button icon="el-icon-refresh" @click="updateData"></el-button>
                        <el-button @click="onPrint">列印</el-button>
                        <el-button size="" @click="onExportExcel">匯出Excel</el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel no-print">
                <div class="panel-body">
                    <search-form>
                        <search-form-item label="表格類型">
                            <el-radio-group v-model="displayMode" @change="onDisplayModeChange">
                                <el-radio-button :label="1">範圍總計</el-radio-button>
                                <el-radio-button :label="2">全年度逐月</el-radio-button>
                            </el-radio-group>
                        </search-form-item>
                        <template  v-if="displayMode === 1">
                            <search-form-item label="傳票日期範圍">
                                <ahDropdownDatePicker v-model="dateRange" @change="updateData" />
                            </search-form-item>
                            <search-form-item label="金額為 0 的科目">
                                <el-radio v-model="isShowZeroItems" :label="true" @change="updateData">顯示</el-radio>
                                <el-radio v-model="isShowZeroItems" :label="false" @change="updateData">隱藏</el-radio>
                            </search-form-item>
                        </template>
                        <template  v-if="displayMode === 2">
                            <search-form-item label="傳票年度">
                                <el-select v-model="queryYear" @change="fetchMonthlyData">
                                    <el-option v-for="year in yearOptions" :key="year" :label="`${year - 1911} 年`" :value="year" />
                                </el-select>
                            </search-form-item>
                            <search-form-item label="金額為 0 的科目">
                                <el-radio v-model="isShowZeroItems" :label="true" @change="fetchMonthlyData">顯示</el-radio>
                                <el-radio v-model="isShowZeroItems" :label="false" @change="fetchMonthlyData">隱藏</el-radio>
                            </search-form-item>
                        </template>
                    </search-form>
                </div>
            </div>
            <div class="ah-panel">
                <div class="panel-body">
                    <TableView
                        ref="table"
                        :isLoading="isLoading"
                        :displayMode="displayMode"
                        :queryYear="queryYear"
                        :startDateTxt="startDateTxt"
                        :endDateTxt="endDateTxt"
                        :monthlyTableData="monthlyTableData"
                        :tableData="tableData"
                        :settlementDateTxt="settlementDateTxt"
                        :closedDateTxt="closedDateTxt"
                        :todayDateTxt="todayDateTxt"
                        :isStartEndDateSameYear="isStartEndDateSameYear"
                        @click="onClickItem"
                    />
                </div>
            </div>
        </div>
        <DetailDialog
            :visible="detailDialog.visible"
            :StartDate="queryParams.StartDate"
            :EndDate="queryParams.EndDate"
            :accno="detailDialog.accno"
            :accnam="detailDialog.accnam"
            @close="onDetailDialogClose"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import ahDropdownDatePicker from '@/components/ui/ahDropdownDatePicker.vue';
import SearchForm from '@/components/ui/SearchForm/SearchForm.vue';
import SearchFormItem from '@/components/ui/SearchForm/SearchFormItem.vue';
import mixins from './mixins';
import { fetchMonthlyRowDatas, updateMonthlyTableData } from './utils';
import { downloadNormalExcel, downloadMonthlyExcel } from './exportExcelUtils';

export default {
    name: 'AccountingStatementPage',
    mixins: [mixins],
    components: {
        ahDropdownDatePicker,
        SearchForm,
        SearchFormItem,
    },
    data() {
        return {
            queryYear: moment().year(),
            yearOptions: [],
            title: null,
        };
    },
    mounted() {
        this.init();
    },
    computed: {
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
    },
    methods: {
        async init() {
            const today = moment();
            this.dateRange = [today.startOf('year').format('x'), today.endOf('year').format('x')];

            const START_YEAR = 2020;
            for (let y = this.queryYear; y >= START_YEAR; y -= 1) {
                this.yearOptions.push(y);
            }

            await this.updateData();
        },

        async fetchMonthlyData() {
            this.dateRange = [
                moment()
                    .year(this.queryYear)
                    .startOf('year')
                    .format('x'),
                moment()
                    .year(this.queryYear)
                    .endOf('year')
                    .format('x'),
            ];
            this.updateQuery();
            await this.getYearlyOpenVoucher();

            try {
                this.isLoading = true;
                const rawData = await fetchMonthlyRowDatas(this.queryYear);
                this.monthlyTableData = updateMonthlyTableData(rawData, this.isShowZeroItems);
            } finally {
                this.isLoading = false;
            }
        },
        onPrint() {
            window.print();
        },

        onDetailDialogClose(refresh) {
            this.detailDialog.visible = false;
            if (refresh) this.updateData();
        },

        onDisplayModeChange(val) {
            if (val === 1) {
                this.dateRange = [
                    moment()
                        .year(this.queryYear)
                        .startOf('year')
                        .format('x'),
                    moment()
                        .year(this.queryYear)
                        .endOf('year')
                        .format('x'),
                ];
                this.updateData();
            } else {
                this.queryYear = moment(+_.head(this.dateRange)).year();
                this.fetchMonthlyData();
            }
        },
        onExportExcel() {
            if (this.displayMode === 1) {
                downloadNormalExcel(this.queryParams.StartDate, this.queryParams.EndDate, this.companyName, null);
            } else {
                downloadMonthlyExcel(this.queryYear, this.companyName, null);
            }
        },
    },
};
</script>
<style lang="scss">
@import "@/assets/scss/vars.scss";

.accounting-statement-page {
    .action-bar {
        margin: 0 0 15px 0;
        padding: 0 30px;
        display: flex;
        align-items: flex-end;
        gap: 20px;

        @media (max-width: 900px) {
            flex-direction: column;
            align-items: flex-start;
        }

        .query-item-block {
            display: flex;
            flex-direction: column;
            margin-right: 10px;
            gap: 5px;
        }
        .datepicker-label {
            font-size: 10pt;
            color: #888;
        }
    }

    @media print {
        .action-bar {
            display: none;
        }
        .no-print {
            display: none;
        }
    }

    .accounting-statement-normal-table-wrapper {
        @media screen {
            max-width: 1000px;
            margin: 0 auto 0 auto;
        }
    }
}
</style>
